import {
  TaskDifficulty,
  TaskImportance,
  TaskStatus,
  TaskUrgency,
} from '../../../api';

export interface SelectOption<T extends string> {
  value: T;
  label: string;
}

function humanize(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export const TASK_STATUS_OPTIONS: SelectOption<TaskStatus>[] = (
  ['archived', 'backlog', 'ongoing', 'active', 'completed'] satisfies TaskStatus[]
).map((value) => ({ value, label: humanize(value) }));

export const TASK_URGENCY_OPTIONS: SelectOption<TaskUrgency>[] = (
  ['urgent', 'not_urgent', 'habit', 'backlog'] satisfies TaskUrgency[]
).map((value) => ({ value, label: humanize(value) }));

export const TASK_IMPORTANCE_OPTIONS: SelectOption<TaskImportance>[] = (
  ['important', 'not_important', 'move_the_needle'] satisfies TaskImportance[]
).map((value) => ({ value, label: humanize(value) }));

export const TASK_DIFFICULTY_OPTIONS: SelectOption<TaskDifficulty>[] = (
  ['flow', 'quick', 'easy', 'personal'] satisfies TaskDifficulty[]
).map((value) => ({ value, label: humanize(value) }));

export const PROJECT_STATUS_OPTIONS = TASK_STATUS_OPTIONS;

export interface ConfigurableField<TForm> {
  key: keyof TForm;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'datetime' | 'date' | 'checkbox' | 'number';
  required?: boolean;
  placeholder?: string;
  options?: Array<SelectOption<string>>;
  description?: string;
}

export interface RelationField<TForm> {
  key: keyof TForm;
  label: string;
  type: 'relation-multi' | 'relation-single';
  emptyLabel?: string;
  createLabel?: string;
  allowNone?: boolean;
  createVisibility?: 'always' | 'empty';
}

export type EntityField<TForm> = ConfigurableField<TForm> | RelationField<TForm>;

export interface TaskFormValues extends Record<string, unknown> {
  name: string;
  urgency: TaskUrgency;
  importance: TaskImportance;
  difficulty: TaskDifficulty;
  status: TaskStatus;
  done: boolean;
  attemptWithAi: boolean;
  timeEstimateMinutes: string;
  parentTaskId: number | null;
  projectIds: number[];
  bucketIds: number[];
}

export const TASK_FIELDS: EntityField<TaskFormValues>[] = [
  { key: 'name', label: 'Task name', type: 'text', required: true },
  { key: 'status', label: 'Status', type: 'select', options: TASK_STATUS_OPTIONS },
  { key: 'urgency', label: 'Urgency', type: 'select', options: TASK_URGENCY_OPTIONS },
  { key: 'importance', label: 'Importance', type: 'select', options: TASK_IMPORTANCE_OPTIONS },
  { key: 'difficulty', label: 'Difficulty', type: 'select', options: TASK_DIFFICULTY_OPTIONS },
  { key: 'done', label: 'Mark as done', type: 'checkbox' },
  {
    key: 'attemptWithAi',
    label: 'Attempt with AI',
    type: 'checkbox',
    description: 'Flag tasks that can be automated by AI tooling.',
  },
  {
    key: 'timeEstimateMinutes',
    label: 'Time estimate (minutes)',
    type: 'number',
    description: 'How long this task should take to complete. Leave blank if unknown.',
  },
  {
    key: 'parentTaskId',
    label: 'Parent task',
    type: 'relation-single',
    emptyLabel: 'No parent task',
    allowNone: true,
  },
  {
    key: 'projectIds',
    label: 'Projects',
    type: 'relation-multi',
    emptyLabel: 'No projects selected',
    createLabel: 'New project',
  },
  {
    key: 'bucketIds',
    label: 'Buckets',
    type: 'relation-multi',
    emptyLabel: 'No buckets selected',
    createLabel: 'New bucket',
  },
];

export interface ProjectFormValues extends Record<string, unknown> {
  name: string;
  status: TaskStatus;
  deadline: string;
  parentProjectId: number | null;
  bucketIds: number[];
}

export const PROJECT_FIELDS: EntityField<ProjectFormValues>[] = [
  { key: 'name', label: 'Project name', type: 'text', required: true },
  { key: 'status', label: 'Status', type: 'select', options: PROJECT_STATUS_OPTIONS },
  { key: 'deadline', label: 'Deadline', type: 'date' },
  {
    key: 'parentProjectId',
    label: 'Parent project',
    type: 'relation-single',
    emptyLabel: 'No parent project.',
    createLabel: 'New project',
    allowNone: true,
  },
  {
    key: 'bucketIds',
    label: 'Buckets',
    type: 'relation-multi',
    emptyLabel: 'No buckets yet.',
    createLabel: 'New bucket',
  },
];

export interface BucketFormValues extends Record<string, unknown> {
  name: string;
  projectIds: number[];
}

export const BUCKET_FIELDS: EntityField<BucketFormValues>[] = [
  { key: 'name', label: 'Bucket name', type: 'text', required: true },
  {
    key: 'projectIds',
    label: 'Projects',
    type: 'relation-multi',
    emptyLabel: 'No projects yet.',
    createLabel: 'New project',
  },
];

export const TASK_INITIAL_VALUES: TaskFormValues = {
  name: '',
  urgency: 'backlog',
  importance: 'not_important',
  difficulty: 'easy',
  status: 'backlog',
  done: false,
  attemptWithAi: false,
  timeEstimateMinutes: '',
  parentTaskId: null,
  projectIds: [],
  bucketIds: [],
};

export const PROJECT_INITIAL_VALUES: ProjectFormValues = {
  name: '',
  status: 'backlog',
  deadline: '',
  parentProjectId: null,
  bucketIds: [],
};

export const BUCKET_INITIAL_VALUES: BucketFormValues = {
  name: '',
  projectIds: [],
};
